import { useEffect, useState } from 'react';
import { Repeat, Check } from 'lucide-react';
import api from '../lib/api';

/**
 * CommunityRecurringPrompt — Q32 Phase B.
 *
 * Centered dialog shown right after the user joins a community that has
 * recurring plans. Offers a one-tap "Save next 4 events to my calendar"
 * action that downloads a combined .ics covering the upcoming occurrences.
 *
 * Props:
 *   open         — bool
 *   communityId  — string
 *   onClose      — () => void
 */
export default function CommunityRecurringPrompt({ open, communityId, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!open || !communityId) return;
    setLoading(true); setDone(false);
    api.get(`/calendar/community/${communityId}/upcoming?limit=4`)
      .then(r => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [open, communityId]);

  if (!open) return null;

  const saveAll = async () => {
    setSaving(true);
    try {
      // Download .ics for each plan in the next-4 list. We sequence the
      // requests to avoid the browser deduping identical download triggers.
      const uniquePlanIds = Array.from(new Set(items.map(r => r.plan.id)));
      for (const pid of uniquePlanIds) {
        try {
          const res = await api.get(`/calendar/ics/plan/${pid}`, { responseType: 'blob' });
          const url = URL.createObjectURL(new Blob([res.data], { type: 'text/calendar' }));
          const a = document.createElement('a');
          a.href = url;
          a.download = `community-${pid}.ics`;
          document.body.appendChild(a); a.click(); a.remove();
          URL.revokeObjectURL(url);
          await new Promise(r => setTimeout(r, 250));
        } catch { /* skip individual plan, continue */ }
      }
      setDone(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[10003] flex items-center justify-center px-6 bg-black/50 backdrop-blur-[2px] animate-fade-in"
      onClick={onClose}
      data-testid="community-recurring-backdrop"
    >
      <div
        className="w-full max-w-[360px] bg-white rounded-3xl shadow-2xl p-6 animate-scale-in"
        onClick={e => e.stopPropagation()}
        data-testid="community-recurring-prompt"
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded-full bg-[#B6E600]/20 text-[#3F5200] flex items-center justify-center">
            <Repeat size={18} strokeWidth={2.6} />
          </div>
          <h3 className="text-base font-extrabold text-[#111]">This community has recurring events.</h3>
        </div>
        <p className="text-sm text-[#6B6E73] mb-4 leading-relaxed">
          Would you like to save the next 4 events to your calendar?
        </p>

        {loading ? (
          <p data-testid="community-recurring-loading" className="text-center text-xs text-[#9B9EA3] py-3">Loading…</p>
        ) : items.length === 0 ? (
          <p data-testid="community-recurring-empty" className="text-center text-xs text-[#9B9EA3] py-3">
            No upcoming occurrences yet.
          </p>
        ) : (
          <ul data-testid="community-recurring-list" className="space-y-1.5 mb-5 max-h-44 overflow-auto">
            {items.map((r, i) => (
              <li key={`${r.plan.id}-${i}`} className="flex items-center gap-2 bg-[#FAFAF7] rounded-lg p-2 border border-black/[0.03]">
                <span className="text-[10px] font-extrabold text-[#3F5200] w-12 flex-shrink-0">
                  {prettyDate(r.date)}
                </span>
                <span className="text-xs font-bold text-[#111] flex-1 truncate">{r.plan.title}</span>
                <span className="text-[10px] text-[#9B9EA3]">{(r.plan.start_time || '').slice(0, 5)}</span>
              </li>
            ))}
          </ul>
        )}

        {done ? (
          <div data-testid="community-recurring-done" className="flex items-center justify-center gap-2 text-[#3F5200] font-bold text-sm py-2 mb-2">
            <Check size={18} /> Saved!
          </div>
        ) : null}

        <div className="flex flex-col gap-2">
          <button
            data-testid="community-recurring-save-all"
            onClick={saveAll}
            disabled={loading || saving || items.length === 0}
            className="w-full bg-[#B6E600] text-[#111] font-bold rounded-full text-sm active:scale-[0.98] disabled:opacity-50"
            style={{ minHeight: 50 }}
          >
            {saving ? 'Saving…' : done ? 'Save again' : 'Save All'}
          </button>
          <button
            data-testid="community-recurring-later"
            onClick={onClose}
            className="w-full bg-transparent text-[#111] font-semibold rounded-full text-sm active:scale-[0.98]"
            style={{ minHeight: 44 }}
          >
            Maybe Later
          </button>
        </div>
      </div>
    </div>
  );
}

function prettyDate(iso) {
  try {
    const d = new Date(iso + 'T00:00:00');
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch { return iso; }
}
